import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../axiosInterceptor';

export type Booking = {
  id: string;
  user_id: string;
  plan_id: string;
  status: string;
  payment_status?: string;
  created_at: string;
  student_name: string;
  student_avatar?: string;
  focus_area: string;
  start_time?: string | null;
  end_time?: string | null;
  joining_link?: string;
  session_status?: string;
  sessions?: Array<{
    id: string;
    scheduled_time: string;
    time?: string;
    date?: { day: number; month: string; year: number };
    status: string;
    duration?: number;
    joining_link?: string;
  }>;
};

type BookingsState = {
  loading: boolean;
  bookings: Booking[];
  error: boolean;
  errorMessage: string;
};

const initialState: BookingsState = {
  loading: false,
  bookings: [],
  error: false,
  errorMessage: '',
};

const normalizeBookings = (data: any): Booking[] => {
  const payload = data?.response ?? data?.data ?? data?.records ?? data ?? [];
  return Array.isArray(payload) ? (payload as Booking[]) : [];
};

export const getBookings = createAsyncThunk<Booking[]>(
  'getBookings',
  async () => {
    const { data } = await api.get('/api/bookings');
    console.log('📦 [GET BOOKINGS] Raw response from backend:', data);
    
    const normalized = normalizeBookings(data);
    
    // Count total sessions across all bookings
    let totalSessions = 0;
    normalized.forEach((booking, idx) => {
      const sessionCount = booking.sessions?.length || 0;
      totalSessions += sessionCount;
      console.log(`📦 [GET BOOKINGS] Booking ${idx + 1}:`, {
        bookingId: booking.id,
        studentName: booking.student_name || booking.user_id,
        sessionsCount: sessionCount,
        sessions: booking.sessions,
      });
    });
    console.log(`📦 [GET BOOKINGS] Total bookings: ${normalized.length}, Total sessions: ${totalSessions}`);
    
    return normalized;
  }
);

const getBookingsSlice = createSlice({
  name: 'getBookings',
  initialState,
  reducers: {
    resetBookings: (state) => {
      state.loading = false;
      state.bookings = [];
      state.error = false;
      state.errorMessage = '';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getBookings.pending, (state) => {
      state.loading = true;
      state.error = false;
      state.errorMessage = '';
    });
    builder.addCase(getBookings.fulfilled, (state, action) => {
      state.loading = false;
      state.bookings = action.payload;
    });
    builder.addCase(getBookings.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
      state.errorMessage = action.error.message ?? 'Failed to load bookings.';
    });
  },
});

export const { resetBookings } = getBookingsSlice.actions;
export default getBookingsSlice.reducer;
